import React, { useEffect, useState } from "react";
import {
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  getAuth,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import app from "../../firebase.js";

function FinanceLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth(app);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] =
    useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] =
    useState("");
  const [loading, setLoading] = useState(false);

  const requestedReturnTo =
    location.state?.returnTo ||
    "/consulting/finance";

  const returnTo = requestedReturnTo.startsWith(
    "/consulting/finance"
  )
    ? requestedReturnTo
    : "/consulting/finance";

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      async (currentUser) => {
        if (!currentUser) {
          return;
        }

        try {
          await currentUser.reload();

          if (currentUser.emailVerified) {
            navigate(returnTo, { replace: true });
          }
        } catch (error) {
          console.error(error);
        }
      }
    );

    return () => unsubscribe();
  }, [auth, navigate, returnTo]);

  const handleLogin = async (event) => {
    event.preventDefault();

    setLoading(true);
    setMessage("");
    setMessageType("");

    try {
      const userCredential =
        await signInWithEmailAndPassword(
          auth,
          email.trim(),
          password
        );

      await userCredential.user.reload();

      if (!userCredential.user.emailVerified) {
        await signOut(auth);

        setMessageType("error");
        setMessage(
          "Votre adresse e-mail n’est pas encore vérifiée. Consultez votre boîte mail avant de vous connecter."
        );

        return;
      }

      setMessageType("success");
      setMessage("Connexion réussie.");

      navigate(returnTo, { replace: true });
    } catch (error) {
      console.error(error);

      setMessageType("error");
      setMessage(
        "Adresse e-mail ou mot de passe incorrect."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      setMessageType("error");
      setMessage(
        "Renseignez votre adresse e-mail avant de demander la réinitialisation."
      );

      return;
    }

    setLoading(true);
    setMessage("");

    try {
      await sendPasswordResetEmail(
        auth,
        email.trim()
      );

      setMessageType("success");
      setMessage(
        "Un e-mail de réinitialisation vient de vous être envoyé."
      );
    } catch (error) {
      console.error(error);

      setMessageType("error");
      setMessage(
        "Impossible d’envoyer l’e-mail de réinitialisation."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email.trim() || !password) {
      setMessageType("error");
      setMessage(
        "Renseignez votre adresse e-mail et votre mot de passe."
      );

      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const userCredential =
        await signInWithEmailAndPassword(
          auth,
          email.trim(),
          password
        );

      await userCredential.user.reload();

      if (userCredential.user.emailVerified) {
        setMessageType("success");
        setMessage(
          "Votre adresse e-mail est déjà vérifiée. Vous pouvez vous connecter."
        );

        await signOut(auth);
        return;
      }

      await sendEmailVerification(
        userCredential.user
      );

      await signOut(auth);

      setMessageType("success");
      setMessage(
        "Un nouvel e-mail de vérification vient de vous être envoyé."
      );
    } catch (error) {
      console.error(error);

      setMessageType("error");
      setMessage(
        "Impossible de renvoyer l’e-mail de vérification. Vérifiez vos identifiants."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <button
        type="button"
        style={styles.backButton}
        onClick={() =>
          navigate("/consulting/finance")
        }
      >
        ← Retour à HDC Finance
      </button>

      <div style={styles.backgroundCircleOne} />
      <div style={styles.backgroundCircleTwo} />

      <main style={styles.card}>
        <div style={styles.brandIcon}>H</div>

        <div style={styles.badge}>
          🔒 Espace sécurisé
        </div>

        <h1 style={styles.title}>
          Connexion HDC Finance
        </h1>

        <p style={styles.subtitle}>
          Connectez-vous pour déposer et suivre vos
          demandes de financement.
        </p>

        <form
          style={styles.form}
          onSubmit={handleLogin}
        >
          <label style={styles.field}>
            <span style={styles.label}>
              Adresse e-mail
            </span>

            <input
              type="email"
              value={email}
              onChange={(event) =>
                setEmail(event.target.value)
              }
              placeholder="exemple@email.com"
              autoComplete="email"
              style={styles.input}
              required
            />
          </label>

          <label style={styles.field}>
            <span style={styles.label}>
              Mot de passe
            </span>

            <div style={styles.passwordWrapper}>
              <input
                type={
                  showPassword
                    ? "text"
                    : "password"
                }
                value={password}
                onChange={(event) =>
                  setPassword(event.target.value)
                }
                placeholder="Votre mot de passe"
                autoComplete="current-password"
                style={styles.passwordInput}
                required
              />

              <button
                type="button"
                style={styles.eyeButton}
                onClick={() =>
                  setShowPassword(
                    (currentValue) =>
                      !currentValue
                  )
                }
                aria-label={
                  showPassword
                    ? "Masquer le mot de passe"
                    : "Afficher le mot de passe"
                }
              >
                {showPassword ? "🙈" : "👁️"}
              </button>
            </div>
          </label>

          <button
            type="button"
            style={styles.forgotButton}
            onClick={handleForgotPassword}
            disabled={loading}
          >
            Mot de passe oublié ?
          </button>

          {message && (
            <div
              style={{
                ...styles.message,
                ...(messageType === "success"
                  ? styles.successMessage
                  : styles.errorMessage),
              }}
            >
              {message}
            </div>
          )}

          <button
            type="submit"
            style={{
              ...styles.loginButton,
              ...(loading
                ? styles.disabledButton
                : {}),
            }}
            disabled={loading}
          >
            {loading
              ? "Connexion en cours…"
              : "Se connecter →"}
          </button>

          <button
            type="button"
            style={styles.verificationButton}
            onClick={handleResendVerification}
            disabled={loading}
          >
            Renvoyer l’e-mail de vérification
          </button>
        </form>

        <div style={styles.separator}>
          <span style={styles.separatorLine} />
          <span style={styles.separatorText}>
            Nouveau sur HDC Finance ?
          </span>
          <span style={styles.separatorLine} />
        </div>

        <button
          type="button"
          style={styles.registerButton}
          onClick={() =>
            navigate(
              "/consulting/finance/inscription",
              {
                state: { returnTo },
              }
            )
          }
        >
          Créer un compte
        </button>

        <p style={styles.securityText}>
          🔐 Vos informations sont protégées et
          accessibles uniquement depuis votre espace.
        </p>
      </main>
    </div>
  );
}

const styles = {
  page: {
    position: "relative",
    minHeight: "100vh",
    padding: "32px 24px 70px",
    boxSizing: "border-box",
    background:
      "linear-gradient(145deg, #050816 0%, #0b1020 60%, #111936 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },

  backButton: {
    position: "absolute",
    top: "30px",
    left: "30px",
    zIndex: 3,
    padding: 0,
    border: "none",
    background: "transparent",
    color: "#cbd5e1",
    fontSize: "13px",
    fontWeight: "800",
    cursor: "pointer",
  },

  backgroundCircleOne: {
    position: "absolute",
    top: "-160px",
    right: "-130px",
    width: "430px",
    height: "430px",
    borderRadius: "50%",
    background:
      "rgba(124, 58, 237, 0.18)",
    filter: "blur(20px)",
  },

  backgroundCircleTwo: {
    position: "absolute",
    bottom: "-180px",
    left: "-130px",
    width: "420px",
    height: "420px",
    borderRadius: "50%",
    background:
      "rgba(37, 99, 235, 0.15)",
    filter: "blur(20px)",
  },

  card: {
    position: "relative",
    zIndex: 2,
    width: "100%",
    maxWidth: "460px",
    padding: "34px",
    boxSizing: "border-box",
    border: "1px solid rgba(255, 255, 255, 0.12)",
    borderRadius: "26px",
    background: "rgba(255, 255, 255, 0.08)",
    boxShadow:
      "0 30px 80px rgba(0, 0, 0, 0.35)",
    backdropFilter: "blur(20px)",
    color: "#ffffff",
    textAlign: "center",
  },

  brandIcon: {
    width: "52px",
    height: "52px",
    margin: "0 auto",
    borderRadius: "16px",
    background:
      "linear-gradient(135deg, #7c3aed, #2563eb)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
    fontSize: "20px",
    fontWeight: "900",
  },

  badge: {
    display: "inline-flex",
    marginTop: "18px",
    padding: "7px 11px",
    borderRadius: "999px",
    background: "rgba(124, 58, 237, 0.15)",
    color: "#c4b5fd",
    fontSize: "10px",
    fontWeight: "900",
  },

  title: {
    margin: "18px 0 0",
    color: "#ffffff",
    fontSize: "30px",
    lineHeight: 1.15,
    fontWeight: "900",
    letterSpacing: "-0.03em",
  },

  subtitle: {
    margin: "12px auto 0",
    maxWidth: "360px",
    color: "#94a3b8",
    fontSize: "13px",
    lineHeight: 1.7,
  },

  form: {
    marginTop: "26px",
    display: "flex",
    flexDirection: "column",
    gap: "15px",
    textAlign: "left",
  },

  field: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },

  label: {
    color: "#cbd5e1",
    fontSize: "11px",
    fontWeight: "800",
  },

  input: {
    width: "100%",
    minHeight: "48px",
    padding: "0 14px",
    boxSizing: "border-box",
    border: "1px solid rgba(255, 255, 255, 0.14)",
    borderRadius: "12px",
    outlineColor: "#7c3aed",
    background: "rgba(2, 6, 23, 0.55)",
    color: "#ffffff",
    fontFamily: "inherit",
    fontSize: "13px",
  },

  passwordWrapper: {
    position: "relative",
  },

  passwordInput: {
    width: "100%",
    minHeight: "48px",
    padding: "0 48px 0 14px",
    boxSizing: "border-box",
    border: "1px solid rgba(255, 255, 255, 0.14)",
    borderRadius: "12px",
    outlineColor: "#7c3aed",
    background: "rgba(2, 6, 23, 0.55)",
    color: "#ffffff",
    fontFamily: "inherit",
    fontSize: "13px",
  },

  eyeButton: {
    position: "absolute",
    top: "50%",
    right: "13px",
    transform: "translateY(-50%)",
    padding: 0,
    border: "none",
    background: "transparent",
    cursor: "pointer",
  },

  forgotButton: {
    alignSelf: "flex-end",
    padding: 0,
    border: "none",
    background: "transparent",
    color: "#a78bfa",
    fontSize: "11px",
    fontWeight: "800",
    cursor: "pointer",
  },

  message: {
    padding: "12px",
    borderRadius: "11px",
    fontSize: "11px",
    lineHeight: 1.6,
  },

  successMessage: {
    border: "1px solid rgba(34, 197, 94, 0.3)",
    background: "rgba(34, 197, 94, 0.12)",
    color: "#86efac",
  },

  errorMessage: {
    border: "1px solid rgba(239, 68, 68, 0.3)",
    background: "rgba(239, 68, 68, 0.12)",
    color: "#fca5a5",
  },

  loginButton: {
    minHeight: "48px",
    border: "none",
    borderRadius: "12px",
    background:
      "linear-gradient(135deg, #7c3aed, #2563eb)",
    color: "#ffffff",
    fontSize: "13px",
    fontWeight: "900",
    cursor: "pointer",
    boxShadow:
      "0 12px 25px rgba(37, 99, 235, 0.24)",
  },

  verificationButton: {
    minHeight: "42px",
    border: "1px solid rgba(255, 255, 255, 0.14)",
    borderRadius: "11px",
    background: "rgba(255, 255, 255, 0.05)",
    color: "#cbd5e1",
    fontSize: "11px",
    fontWeight: "800",
    cursor: "pointer",
  },

  disabledButton: {
    opacity: 0.65,
    cursor: "not-allowed",
  },

  separator: {
    marginTop: "24px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },

  separatorLine: {
    flex: 1,
    height: "1px",
    background: "rgba(255, 255, 255, 0.1)",
  },

  separatorText: {
    color: "#64748b",
    fontSize: "10px",
  },

  registerButton: {
    width: "100%",
    minHeight: "45px",
    marginTop: "18px",
    border: "1px solid rgba(167, 139, 250, 0.35)",
    borderRadius: "12px",
    background: "rgba(124, 58, 237, 0.08)",
    color: "#c4b5fd",
    fontSize: "12px",
    fontWeight: "900",
    cursor: "pointer",
  },

  securityText: {
    margin: "20px 0 0",
    color: "#64748b",
    fontSize: "10px",
    lineHeight: 1.6,
  },
};

export default FinanceLogin;